import { MOONPAY_WIDGET_CONTAINER_ID } from 'src/constants';
import {
  MoonPayBuyConfig,
  MoonPayClaimWidgetConfig,
  MoonPayConsumerKycConfig,
  MoonPayDebug,
  MoonPayEnvironment,
  MoonPayLegacyConfig,
  MoonPayNftWidgetConfig,
  MoonPaySellConfig,
  MoonPaySwapConfig,
  MoonPaySwapsCustomerSetupConfig,
  MoonPayTopUpBalanceLedgerWidgetConfig,
  MoonPayTransactionTrackerConfig,
  MoonPayWidgetProps,
  MoonPayWithdrawLedgerWidgetConfig,
  TFlow,
} from './types';

type CommonProps<T extends TFlow> = {
  debug: MoonPayDebug;
  environment: MoonPayEnvironment;
  props: MoonPayWidgetProps<T>;
};

type ConfigConversionParams<T extends TFlow> = T extends 'legacy'
  ? Omit<CommonProps<T>, 'apiKey' | 'signature'>
  : CommonProps<T> & {
      apiKey: string;
      signature: string;
    };

export const getBuyConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'buy'>): MoonPayBuyConfig => {
  const {
    // params
    currencyCode,
    defaultCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
    // other
    overlayNode,
  } = props;

  const params: MoonPayBuyConfig['params'] = {
    apiKey,
    signature,
    currencyCode,
    defaultCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
  };

  const handlers: MoonPayBuyConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'buy',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
    overlayNode,
  };
};

export const getSellConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'sell'>): MoonPaySellConfig => {
  const {
    // params
    currencyCode,
    defaultCurrencyCode,
    defaultBaseCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params: MoonPaySellConfig['params'] = {
    apiKey,
    signature,
    currencyCode,
    defaultCurrencyCode,
    defaultBaseCurrencyCode,
    walletAddress,
    walletAddressTag,
    walletAddresses,
    walletAddressTags,
    colorCode,
    theme,
    themeId,
    language,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyAmount,
    lockAmount,
    email,
    externalTransactionId,
    externalCustomerId,
    paymentMethod,
    redirectURL,
    showAllCurrencies,
    showOnlyCurrencies,
    showWalletAddressForm,
    unsupportedRegionRedirectUrl,
    skipUnsupportedRegionScreen,
  };

  const handlers: MoonPaySellConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'sell',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getSwapsCustomerSetupConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
}: ConfigConversionParams<
  'swapsCustomerSetup'
>): MoonPaySwapsCustomerSetupConfig => {
  const {
    amount,
    amountCurrencyCode,
    theme,
    themeId,
    // handlers
    onAuthToken,
    onLogin,
    onInitiateDeposit,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    amount,
    amountCurrencyCode,
    theme,
    themeId,
  };

  const handlers = {
    onAuthToken,
    onLogin,
    onInitiateDeposit,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
  };

  return {
    flow: 'swapsCustomerSetup',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getConsumerKycConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
}: ConfigConversionParams<'consumerKyc'>): MoonPayConsumerKycConfig => {
  const {
    amount,
    amountCurrencyCode,
    theme,
    themeId,
    // handlers
    onLogin,
    onInitiateDeposit,
    onConsumerKycComplete,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    amount,
    amountCurrencyCode,
    theme,
    themeId,
  };

  const handlers = {
    onLogin,
    onInitiateDeposit,
    onConsumerKycComplete,
    onKmsWalletsCreated,
    onTransactionCompleted,
    onUnsupportedRegion,
  };

  return {
    flow: 'consumerKyc',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getNftConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'nft'>): MoonPayNftWidgetConfig => {
  const {
    // params
    contractAddress,
    tokenId,
    listingId,
    externalTransactionId,
    baseCurrencyCode,
    redirectURL,
    walletAddress,
    subPartnerName,
    // handlers
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    contractAddress,
    tokenId,
    listingId,
    externalTransactionId,
    baseCurrencyCode,
    redirectURL,
    walletAddress,
    subPartnerName,
  };

  const handlers = {
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
  };

  return {
    flow: 'nft',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getClaimConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'claim'>): MoonPayClaimWidgetConfig => {
  const {
    contractId,
    tokenId,
    initialScreen,
    // handlers
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    contractId,
    tokenId,
    initialScreen,
  };

  const handlers = {
    onAuthToken,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onCloseOverlay,
  };

  return {
    flow: 'claim',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getTopUpLedgerConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'topup'>): MoonPayTopUpBalanceLedgerWidgetConfig => {
  const {
    // params
    externalCustomerId,
    theme,
    themeId,
    currencyCode,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    externalCustomerId,
    theme,
    themeId,
    currencyCode,
  };

  const handlers = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'topup',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getWithdrawLedgerConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'withdraw'>): MoonPayWithdrawLedgerWidgetConfig => {
  const {
    // params
    externalCustomerId,
    theme,
    themeId,
    baseCurrencyCode,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    apiKey,
    signature,
    externalCustomerId,
    theme,
    themeId,
    baseCurrencyCode,
  };

  const handlers = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'withdraw',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getLegacyConfigFromProps = ({
  props,
  debug,
  environment,
}: ConfigConversionParams<'legacy'>): MoonPayLegacyConfig => {
  const {
    // params
    url,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params = {
    url,
  };

  const handlers = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'legacy',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getSwapConfigFromProps = ({
  props,
  apiKey,
  debug,
  environment,
  signature,
}: ConfigConversionParams<'swap'>): MoonPaySwapConfig => {
  const {
    // params
    walletAddress,
    walletAddressTag,
    refundWalletAddress,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyCode,
    quoteCurrencyAmount,
    colorCode,
    theme,
    themeId,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params: MoonPaySwapConfig['params'] = {
    apiKey,
    signature,
    walletAddress,
    walletAddressTag,
    refundWalletAddress,
    baseCurrencyCode,
    baseCurrencyAmount,
    quoteCurrencyCode,
    quoteCurrencyAmount,
    colorCode,
    theme,
    themeId,
  };

  const handlers: MoonPayBuyConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'swap',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    useWarnBeforeRefresh,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
  };
};

export const getTransactionTrackerConfigFromProps = ({
  props,
  debug,
  environment,
}: ConfigConversionParams<
  'transactionTracker'
>): MoonPayTransactionTrackerConfig => {
  const {
    // params
    transactionId,
    colorCode,
    theme,
    themeId,
    // handlers
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
    // auth
    auth,
    useWarnBeforeRefresh,
  } = props;

  const params: MoonPayTransactionTrackerConfig['params'] = {
    transactionId,
    colorCode,
    theme,
    themeId,
  };

  const handlers: MoonPayBuyConfig['handlers'] = {
    onAuthToken,
    onInitiateDeposit,
    onKmsWalletsCreated,
    onLogin,
    onTransactionCompleted,
    onUnsupportedRegion,
    onSwapsCustomerSetupComplete,
    onCloseOverlay,
  };

  return {
    flow: 'transactionTracker',
    variant: props.variant,
    environment,
    params,
    debug,
    handlers,
    auth,
    containerNodeSelector: `#${MOONPAY_WIDGET_CONTAINER_ID}`,
    useWarnBeforeRefresh,
  };
};
